import type { AudioVisualFrame, HandFrame, HandLandmark, HandSample, HandVoice, InstrumentControl, LadderRange, LoopLayerState, OverlayExtras, PinchState, TrackingOverlayPort } from '../domain/types'
import { LADDER_BOTTOM, LADDER_TOP, PENTATONIC_INTERVALS, palmSpan } from '../music/thereminMapper'
import { NoteStrings, NOTE_STRING_STYLES } from './noteStrings'
const CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [5, 6], [6, 7], [7, 8], [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16], [13, 17], [17, 18], [18, 19], [19, 20], [0, 17]
] as const
// Colours belong to spatial slots (0 = further right on screen), not to left/right labels.
const SLOT_COLORS = ['#f4bd62', '#55d5e8'] as const

export function createTrackingOverlay(canvas:HTMLCanvasElement,video:HTMLVideoElement,readAudio?: (notes: readonly number[]) => AudioVisualFrame | undefined):TrackingOverlayPort {
  const context=canvas.getContext('2d'),reducedMotion=window.matchMedia?.('(prefers-reduced-motion: reduce)').matches??false
  const strings=new NoteStrings()
  let lastFrame:HandFrame|null=null,lastControl=emptyControl(),lastPinches:readonly PinchState[]=[idlePinch(),idlePinch()],lastLayers:readonly LoopLayerState[]=[]
  let extras:OverlayExtras={commands:[],lesson:null,flash:null},flashAt=0,animation=0,disposed=false
  const rect=():Rect=>video.srcObject?coverRect(canvas.width,canvas.height,video.videoWidth||16,video.videoHeight||9):{x:0,y:0,width:canvas.width,height:canvas.height}
  const resize=()=>{const b=canvas.getBoundingClientRect(),ratio=Math.min(devicePixelRatio||1,2);const w=Math.max(1,Math.round(b.width*ratio)),h=Math.max(1,Math.round(b.height*ratio));if(canvas.width!==w||canvas.height!==h){canvas.width=w;canvas.height=h}}
  const handFor=(slot:number)=>lastFrame?.hands.find(h=>h.slot===slot&&h.landmarks.length>=21)??null
  const draw=(at:number)=>{
    if(!context)return
    const width=canvas.width,height=canvas.height,r=rect(),ui=canvas.dataset.chrome!=='hidden'
    context.clearRect(0,0,width,height);strings.expire(at)
    const register=lastControl.voices[0].targetMidi-(PENTATONIC_INTERVALS[lastControl.voices[0].noteIndex]??0)
    strings.setAudio(readAudio?.(PENTATONIC_INTERVALS.map(interval=>register+interval)),at)
    drawLadder(context,width,height,r,lastControl,strings,at,reducedMotion)
    if(ui&&extras.lesson)drawLessonTarget(context,width,r,lastControl,extras.lesson.noteIndex,extras.lesson.label,at,handFor(0)??handFor(1))
    for(const hand of lastFrame?.hands??[]){const slot=hand.slot??0,color=SLOT_COLORS[slot]??SLOT_COLORS[0];drawHand(context,hand.landmarks,r,width,color,lastControl.voices.find(v=>v.slot===slot));drawPinch(context,hand.landmarks,r,lastPinches[slot],color);const ring=extras.commands.find(c=>c.slot===slot);if(ring)drawCommandRing(context,point(hand.landmarks[8]!,r),width,color,ring.label,ring.progress)}
    if(ui&&extras.flash&&at-flashAt<1400){context.save();context.globalAlpha=Math.max(0,1-(at-flashAt)/1400);context.fillStyle='#eee9df';context.textAlign='center';context.font='28px Georgia,serif';context.fillText(extras.flash,width/2,height*.3);context.restore()}
    if(ui&&!reducedMotion)drawLoopProgress(context,width,height,lastLayers)
  }
  const animate=(at:number)=>{if(disposed)return;draw(at);animation=requestAnimationFrame(animate)}
  const observer=typeof ResizeObserver==='undefined'?null:new ResizeObserver(()=>{resize();draw(performance.now())});observer?.observe(canvas)
  resize();if(!reducedMotion)animation=requestAnimationFrame(animate)
  return {
    resize(){resize();draw(performance.now())},
    render(frame,control,pinches,layers,nextExtras){
      lastFrame=frame;lastControl=control;lastPinches=pinches;lastLayers=layers
      if(nextExtras){if(nextExtras.flash!==extras.flash)flashAt=performance.now();extras=nextExtras}
      const r=rect()
      strings.update(control.voices.filter(v=>v.active).map(v=>{const hand=handFor(v.slot);return{slot:v.slot,note:v.noteIndex,gate:v.gate,x:hand?point(hand.landmarks[8]!,r).x/canvas.width:.2+v.timbre*.6,span:hand?palmSpan(hand.landmarks):.2}}),performance.now())
      if(reducedMotion)draw(performance.now())
    },
    dispose(){disposed=true;observer?.disconnect();cancelAnimationFrame(animation);strings.reset();context?.clearRect(0,0,canvas.width,canvas.height)}
  }
}
function drawLadder(context:CanvasRenderingContext2D,width:number,height:number,rect:Rect,control:InstrumentControl,strings:NoteStrings,at:number,reduced:boolean){
  const register=control.voices[0].targetMidi-(PENTATONIC_INTERVALS[control.voices[0].noteIndex]??0)
  context.save();context.font=`600 ${Math.max(11,width*.009)}px ui-monospace,monospace`
  for(let index=0;index<PENTATONIC_INTERVALS.length;index++){
    const y=ladderYFor(rect,index,control.ladder),active=strings.activity(index,at),energy=strings.energy(index)
    const color=NOTE_STRING_STYLES[index]!.color
    const gradient=context.createLinearGradient(width*.045,0,width*.985,0)
    gradient.addColorStop(0,colorWithAlpha(color,.35));gradient.addColorStop(.12,colorWithAlpha(color,active?1:.68));gradient.addColorStop(.87,colorWithAlpha(color,active?1:.68));gradient.addColorStop(1,colorWithAlpha(color,.12))
    context.strokeStyle=gradient;context.lineWidth=Math.max(1,width*.00085)
    context.beginPath();context.moveTo(width*.045,y);context.lineTo(width*.985,y);context.stroke()
    if(active&&!reduced){
      const trace=(amount:number,phase:number)=>{
        context.beginPath()
        for(let step=0;step<=160;step++){
          const x=.045+.94*step/160,dy=strings.sample(index,x,at,false,phase)*height*amount
          if(step===0)context.moveTo(x*width,y+dy);else context.lineTo(x*width,y+dy)
        }
      }
      // A diffuse under-stroke, crisp core and prismatic filaments keep the wave legible over camera pixels.
      context.shadowColor=color;context.shadowBlur=12+energy*18;context.lineWidth=3+energy*4;context.globalAlpha=.36
      trace(1,0);context.stroke();context.shadowBlur=0
      const filaments=[{scale:1,phase:0,alpha:1},{scale:.76,phase:.45,alpha:.65},{scale:-.68,phase:.9,alpha:.52},{scale:.48,phase:1.6,alpha:.38},{scale:-.35,phase:2.4,alpha:.3}]
      for(const [pass,filament] of filaments.entries()){
        context.strokeStyle=pass===3?'#f4ffff':pass===2?NOTE_STRING_STYLES[(index+2)%NOTE_STRING_STYLES.length]!.color:gradient
        context.lineWidth=pass===0?1.3+energy*1.2:1
        context.globalAlpha=filament.alpha
        trace(filament.scale,filament.phase);context.stroke()
      }
    }
    context.globalAlpha=1;context.fillStyle=color;context.textAlign='left'
    context.shadowColor='#030609';context.shadowBlur=4
    context.fillText(noteName(register+(PENTATONIC_INTERVALS[index]??0)),width*.009,y+width*.003)
    context.shadowBlur=0
  }
  context.restore()
}
function ladderYFor(rect:Rect,index:number,ladder:LadderRange):number{return rect.y+rect.height*(ladder.bottom-index/(PENTATONIC_INTERVALS.length-1)*(ladder.bottom-ladder.top))}
function drawHand(context: CanvasRenderingContext2D, landmarks: HandLandmark[], rect: Rect, canvasWidth: number, color: string, voice: HandVoice | undefined): void {
  if (landmarks.length < 21) return
  const width = Math.max(rect.width, 1)
  context.save()
  context.lineCap = 'round'; context.lineJoin = 'round'; context.lineWidth = Math.max(2, width * 0.0024)
  context.strokeStyle = colorWithAlpha(color, 0.75); context.shadowColor = color; context.shadowBlur = width * 0.008
  for (const [from, to] of CONNECTIONS) {
    const a = point(landmarks[from]!, rect); const b = point(landmarks[to]!, rect)
    context.beginPath(); context.moveTo(a.x, a.y); context.lineTo(b.x, b.y); context.stroke()
  }
  context.shadowBlur = 0
  for (let index = 0; index < landmarks.length; index += 1) {
    if (index === 8) continue
    const position = point(landmarks[index]!, rect)
    context.fillStyle = index === 4 ? '#fff9e8' : colorWithAlpha(color, 0.85)
    context.beginPath(); context.arc(position.x, position.y, index === 4 ? width * 0.005 : width * 0.003, 0, Math.PI * 2); context.fill()
  }

  // The index fingertip is the pointer: a bright cursor with a guide line back to the ladder.
  const tip = point(landmarks[8]!, rect)
  const playing = Boolean(voice?.gate)
  context.strokeStyle = colorWithAlpha(color, playing ? 0.45 : 0.22)
  context.lineWidth = Math.max(1, width * 0.001)
  context.setLineDash([width * 0.006, width * 0.006])
  context.beginPath(); context.moveTo(canvasWidth * 0.045, tip.y); context.lineTo(tip.x, tip.y); context.stroke()
  context.setLineDash([])
  context.shadowColor = color
  context.shadowBlur = playing ? width * 0.018 : width * 0.006
  context.fillStyle = playing ? '#ffffff' : colorWithAlpha(color, 0.9)
  context.beginPath(); context.arc(tip.x, tip.y, width * (playing ? 0.009 : 0.006), 0, Math.PI * 2); context.fill()
  context.shadowBlur = 0
  context.strokeStyle = color
  context.lineWidth = Math.max(1.5, width * 0.0016)
  context.beginPath(); context.arc(tip.x, tip.y, width * (playing ? 0.016 : 0.011), 0, Math.PI * 2); context.stroke()

  if (voice) {
    context.fillStyle = playing ? color : 'rgba(255,255,255,.45)'
    context.font = `700 ${Math.max(13, width * 0.014)}px ui-monospace, monospace`
    context.textAlign = 'center'
    context.fillText(playing ? noteName(voice.targetMidi) : 'FIST · RESTING', tip.x, tip.y - width * 0.028)
  }
  context.restore()
}

function drawPinch(context: CanvasRenderingContext2D, landmarks: HandLandmark[], rect: Rect, pinch: PinchState | undefined, color: string): void {
  if (!pinch || landmarks.length < 21) return
  if (pinch.state !== 'contact' && pinch.state !== 'accepted' && pinch.state !== 'held') return
  const thumb = point(landmarks[4]!, rect); const index = point(landmarks[8]!, rect)
  const center = { x: (thumb.x + index.x) / 2, y: (thumb.y + index.y) / 2 }
  const accepted = pinch.state === 'accepted'
  const held = pinch.state === 'held'
  context.save()
  context.strokeStyle = accepted || held ? '#ffffff' : color
  context.lineWidth = accepted ? 5 : 2
  const radius = rect.width * (accepted ? 0.028 : 0.018)
  context.beginPath(); context.arc(center.x, center.y, radius, 0, Math.PI * 2); context.stroke()
  if (pinch.holdProgress > 0.08 && !accepted) {
    // The hold arc: when it closes, the echoes are hushed.
    context.lineWidth = 4
    context.beginPath(); context.arc(center.x, center.y, radius * 1.5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pinch.holdProgress); context.stroke()
  }
  context.fillStyle = accepted || held ? '#ffffff' : color
  context.font = `700 ${Math.max(10, rect.width * 0.009)}px ui-monospace, monospace`; context.textAlign = 'center'
  context.fillText(accepted ? 'LOOP!' : held ? 'HUSH' : pinch.holdProgress > 0.3 ? 'HOLD TO HUSH' : 'PINCH', center.x, center.y + rect.height * 0.06)
  context.restore()
}

function drawCommandRing(context: CanvasRenderingContext2D, tip: { x: number; y: number }, width: number, color: string, label: string, progress: number): void {
  const radius = width * 0.028
  context.save()
  context.strokeStyle = 'rgba(255,255,255,.28)'
  context.lineWidth = Math.max(2, width * 0.0025)
  context.beginPath(); context.arc(tip.x, tip.y, radius, 0, Math.PI * 2); context.stroke()
  context.strokeStyle = progress >= 1 ? '#ffffff' : color
  context.lineWidth = Math.max(3, width * 0.004)
  context.shadowColor = color
  context.shadowBlur = width * 0.01
  context.beginPath(); context.arc(tip.x, tip.y, radius, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, progress)); context.stroke()
  context.shadowBlur = 0
  context.fillStyle = progress >= 1 ? '#ffffff' : color
  context.font = `700 ${Math.max(10, width * 0.01)}px ui-monospace, monospace`
  context.textAlign = 'center'
  context.fillText(label, tip.x, tip.y + radius + width * 0.016)
  context.restore()
}

function drawLessonTarget(context: CanvasRenderingContext2D, width: number, rect: Rect, control: InstrumentControl, noteIndex: number, label: string, timestampMs: number, hand: HandSample | null): void {
  const y = ladderYFor(rect, noteIndex, control.ladder)
  const x = hand ? point(hand.landmarks[8]!, rect).x : width * 0.5
  const pulse = Math.sin(timestampMs * 0.005) * 0.5 + 0.5
  context.save()
  context.globalCompositeOperation = 'lighter'
  context.strokeStyle = `rgba(255,248,236,${0.45 + pulse * 0.35})`
  context.lineWidth = Math.max(2, width * 0.003)
  context.setLineDash([width * 0.01, width * 0.008])
  context.beginPath(); context.moveTo(width * 0.045, y); context.lineTo(width * 0.985, y); context.stroke()
  context.setLineDash([])
  context.shadowColor = '#fff8ec'
  context.shadowBlur = width * (0.012 + pulse * 0.01)
  context.beginPath(); context.arc(x, y, width * (0.02 + pulse * 0.006), 0, Math.PI * 2); context.stroke()
  context.beginPath(); context.arc(x, y, width * 0.008, 0, Math.PI * 2); context.stroke()
  context.shadowBlur = 0
  context.globalCompositeOperation = 'source-over'
  context.fillStyle = '#fff8ec'
  context.font = `400 ${Math.max(22, width * 0.03)}px Iowan Old Style, Baskerville, Georgia, serif`
  context.textAlign = 'center'
  context.fillText(label, x, y - width * 0.036)
  context.restore()
}

function drawLoopProgress(context: CanvasRenderingContext2D, width: number, height: number, layers: readonly LoopLayerState[]): void {
  const playing = layers.filter((layer) => layer.status !== 'empty')
  if (playing.length === 0) return
  context.fillStyle = 'rgba(255,255,255,.8)'
  context.fillRect(width * 0.025, height * 0.965, width * 0.95 * (playing[0]?.loopPhase ?? 0), Math.max(2, height * 0.004))
}

interface Rect { x: number; y: number; width: number; height: number }

export function videoRect(width: number, height: number, videoWidth: number, videoHeight: number): Rect {
  const sourceRatio = videoWidth / Math.max(1, videoHeight); const targetRatio = width / Math.max(1, height)
  if (sourceRatio > targetRatio) { const renderedHeight = width / sourceRatio; return { x: 0, y: (height - renderedHeight) / 2, width, height: renderedHeight } }
  const renderedWidth = height * sourceRatio
  return { x: (width - renderedWidth) / 2, y: 0, width: renderedWidth, height }
}

export function coverRect(width: number, height: number, videoWidth: number, videoHeight: number): Rect {
  const scale = Math.max(width / Math.max(1, videoWidth), height / Math.max(1, videoHeight))
  const renderedWidth = videoWidth * scale
  const renderedHeight = videoHeight * scale
  return { x: (width - renderedWidth) / 2, y: (height - renderedHeight) / 2, width: renderedWidth, height: renderedHeight }
}

function point(landmark: HandLandmark, rect: Rect): { x: number; y: number } {
  return { x: rect.x + landmark.x * rect.width, y: rect.y + landmark.y * rect.height }
}


function colorWithAlpha(hex: string, alpha: number): string {
  const value = parseInt(hex.slice(1), 16)
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`
}

function noteName(midi: number): string {
  const names = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B']
  return `${names[(midi % 12 + 12) % 12]}${Math.floor(midi / 12) - 1}`
}

function idlePinch(): PinchState {
  return { state: 'armed', tap: false, hold: false, holdProgress: 0, distanceRatio: 1, confidence: 0 }
}

function emptyControl(): InstrumentControl {
  return {
    voices: [
      { slot: 0, active: false, gate: false, targetMidi: 60, pitchHz: 261.63, noteIndex: 0, level: 0, timbre: 0.5, pan: 0, handedness: null },
      { slot: 1, active: false, gate: false, targetMidi: 60, pitchHz: 261.63, noteIndex: 0, level: 0, timbre: 0.5, pan: 0, handedness: null }
    ],
    events: [],
    ambience: 0.5,
    ladder: { top: LADDER_TOP, bottom: LADDER_BOTTOM },
    timestampMs: 0
  }
}
